import { formatRiel, formatUsd, type Product } from "@moomoo/core";
import type { Translate } from "../lib/i18n.js";
import type { AppState } from "../lib/store.js";
import { Counter, ScreenBar, tileFor } from "./bits.js";

export function Cart({
  state,
  t,
  lang,
  onBump,
  onBack,
  onCheckout,
}: {
  state: AppState;
  t: Translate;
  lang: "en" | "kh";
  onBump: (product: Product, direction: 1 | -1) => void;
  onBack: () => void;
  onCheckout: () => void;
}) {
  const { totals, channel, quote } = state;

  if (totals.lines.length === 0) {
    return (
      <>
        <ScreenBar title={t("yourCart")} onBack={onBack} backLabel={t("back")} />
        <div className="empty">
          <b>{t("cartEmpty")}</b>
          {t("cartEmptySub")}
        </div>
        <div className="pad">
          <button type="button" className="btn quiet" onClick={onBack}>
            {t("backToShop")}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <ScreenBar
        title={t("yourCart")}
        onBack={onBack}
        backLabel={t("back")}
        aside={`${totals.bottles} ${t("bottles")}`}
      />
      <div className="pad tall">
        {totals.lines.map((line) => (
          <div className="row" key={line.product.code}>
            <span className="thumb">
              <img src={tileFor(line.product.code)} alt="" width={58} height={58} loading="lazy" />
            </span>
            <span className="mid">
              <span className="t">
                {lang === "kh" ? line.product.nameKh : line.product.nameEn}
              </span>
              <br />
              <span className="s">
                {formatUsd(line.product.price)} × {line.qty}
              </span>
            </span>
            <Counter
              qty={line.qty}
              label={line.product.nameEn}
              onDown={() => onBump(line.product, -1)}
              onUp={() => onBump(line.product, 1)}
            />
            <span className="amt">{formatUsd(line.lineTotal)}</span>
          </div>
        ))}

        <button type="button" className="linkbtn" onClick={onBack} style={{ marginTop: 8 }}>
          + {t("addMore")}
        </button>

        <div className="summary">
          <div className="tot">
            <span>{t("subtotal")}</span>
            <span>{formatUsd(totals.subtotal)}</span>
          </div>
          <div className="tot">
            <span>
              {t("delivery")}
              {channel === "retail" && quote ? ` · ${quote.km} km` : ""}
            </span>
            <span>
              {channel === "wholesale" ? t("free") : quote ? formatUsd(totals.delivery) : t("atCheckout")}
            </span>
          </div>
          <div className="tot grand">
            <span>{t("total")}</span>
            <span>{formatUsd(totals.total)}</span>
          </div>
          <p className="note" style={{ margin: "2px 0 0" }}>
            {formatRiel(totals.total)}
            {channel === "retail" && !quote ? ` · ${t("pricedAtCheckout")}` : ""}
          </p>
        </div>

        {channel === "wholesale" ? <p className="note">{t("noFeeWholesale")}</p> : null}

        <div className="grow" />
        <div className="actionbar">
          <div className="sum">
            <small>{t("total")}</small>
            <b>{formatUsd(totals.total)}</b>
          </div>
          <button type="button" className="btn" onClick={onCheckout}>
            {t("checkout")}
          </button>
        </div>
      </div>
    </>
  );
}
